import { For, Match, Show, Switch, createEffect, createMemo } from "solid-js";
import { CategoryCardType } from "../../data/brand_categories";
import { HubbleBrandProduct } from "../../data/products";
import {
  HubbleFullSvg,
  assetVictoryIcon,
  backArrowLeft,
  defaultCoin,
  donationIcon,
  donationIconColored,
  dotMenu,
  kettoSDKLoadingImage,
  placeholderCard,
  seeBrandsArrow,
} from "../../assets/assets";
import {
  goBack,
  HubbleRoute,
  PageAlignment,
  pushPage,
} from "../../shared_states/modal";
import { roundOffCoins, toIndianNumber, toRupees } from "../../utils/number";
import { Banners } from "../../widgets/banners";
import { ThreeDotLoader } from "../../widgets/button";
import HubbleImage from "../../widgets/hubble_image";
import { PhosphorIcon } from "../../widgets/icons";
import { SimpleRetryView } from "../../widgets/resource_loader";
import { IconButton, LargeProductCard } from "./home_helper";
import { HomeMenu } from "./home_menu";
import { SearchOverlay, openSearchPage, selectASearchCategory } from "./search";
import { routerLog } from "~/utils/logging";
import { useClassicContext } from "~/data/classic/classic_context";
import { openVoucherL2 } from "../screens";
import { HubbleBranding } from "~/widgets/branding";
import { hostOnAppLoaded } from "~/utils/sdk/host_actions";
import { brandsCount } from "~/data/static";
import { postCloseEventToAllExternalListeners } from "~/utils/popstate_listener";
import { config } from "~/data/config";
import { HubbleEvent, sdkEventManager } from "~/data/events";
import { clientLogoDescriptions } from "~/data/partner_config";
import { BankTransferDetails } from "../bank_transfer/bank_transfer_details";
import { HomepageMetaData } from "~/server/types/brand";
import HorizontalSection from "./horizontal_sections";
import LeftArrow from "~/assets/svg_icons/left_arrow";
import HorizontalThreeDot from "~/assets/svg_icons/horizontal_three_dot";

function CategoryCardScrollItem({ card }: { card: CategoryCardType }) {
  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;

  return (
    <div
      class="box-border flex h-[100px] w-[132px] cursor-pointer flex-col items-stretch rounded-2xl border border-baseTertiaryMedium p-3 shadow-categoryCard"
      onClick={() => {
        selectASearchCategory(card.categoryName);
      }}
    >
      <span class="title-secondary line-clamp-1 text-ellipsis  text-bold text-textDark">
        {card.categoryTitle}
      </span>
      <div class="flex flex-col">
        <div>
          <Show when={context.isClientDonationType()}>
            <span class="text-f12 text-textNormal">{"Donate "}</span>
          </Show>
          <Show when={clientConfig?.payoutsEnabled}>
            <span class="body-small text-f12 text-textNormal">
              {card.brandsCount} brands
            </span>
          </Show>
          <Show
            when={
              !context.isClientDonationType() && !clientConfig?.payoutsEnabled
            }
          >
            <span class="body-small text-f12 text-textNormal">
              Up to {card.maxDiscountPercent}%
            </span>
          </Show>
        </div>
        <div class="flex flex-row items-center justify-end gap-1 pr-2">
          <For each={card.brandLogoUrls}>
            {(logoUrl) => (
              <img
                src={logoUrl}
                class="h-8 translate-x-[12px] translate-y-[4px] self-end rounded-full"
                alt={card.categoryTitle}
              />
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

function CategoryCardScroll(props: { categories: CategoryCardType[] }) {
  // const cards = props.categories.reduce((acc, column) => {
  //   return acc + (column.brandCards ? column.brandCards.length : 0);
  // }, 0);

  // const totalSlides = Math.ceil(cards / 2);

  // const [slider, { current, next, prev, moveTo }] = createSlider({
  //   loop: totalSlides ?? 0 <= 1 ? false : true,
  //   drag: true,
  //   dragSpeed: 2,
  //   slides: {
  //     perView: 3,
  //     origin: "auto",
  //     spacing: 0,
  //   },
  // });

  return (
    <Show when={props.categories?.length ?? 0 > 0} fallback={<></>}>
      <div class="relative">
        <div
          // use:slider
          class="no-scrollbar mt-1.5 flex flex-row gap-3 overflow-auto scroll-smooth px-5 py-3"
        >
          <div class="grid auto-cols-max grid-flow-col grid-rows-2 gap-3">
            <For each={props.categories}>
              {(card) => <CategoryCardScrollItem card={card} />}
            </For>
          </div>
          <SeeAllBrandsNudge />
        </div>
      </div>
      <div class="mx-5 flex flex-row items-center justify-center gap-0 py-4">
        <hr class="h-px w-full border-0 bg-gray-200" />
        {/* <div class="relative bg-gray-200 rounded-md w-32 h-1 overflow-hidden">
          <div
            class="top-0 left-0 absolute bg-baseDark rounded-md max-w-2 h-full transition-all duration-500 ease-in-out"
            style={{
              width: `${((current() + 1) / totalSlides) * 100}%`,
              left: `${(current() / totalSlides) * 100}%`,
            }}
          ></div>
        </div> */}
        <hr class="h-px w-full border-0 bg-gray-200" />
      </div>
    </Show>
  );
}

function SeeAllBrandsNudge() {
  return (
    <div
      class="flex w-full flex-1 grow flex-col items-center justify-center gap-1 py-5"
      onClick={openSearchPage}
    >
      <img
        src={
          "https://gullak-assets.s3.ap-south-1.amazonaws.com/category-images/promos/cat_promo_see_all_cta.png"
        }
        class="h-[52px]"
        alt="see all brands"
      />
      <span class="title-primary text-nowrap text-center text-f17 text-textDark">
        {brandsCount + "+ brands"}
      </span>
      <div class="flex w-full items-center justify-center gap-2">
        <span class="title-secondary text-nowrap text-mediumBold text-textNormal">
          SEE ALL
        </span>
        <img
          src={
            "https://gullak-assets.s3.ap-south-1.amazonaws.com/icons/next_icon.png"
          }
          class="h-4"
        />
      </div>
    </div>
  );
}

function AppHeader() {
  const context = useClassicContext();
  const contextData = context.data;
  const v4Home = contextData.isLoggedIn ? contextData.v4HomePage : null;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;

  const clientId = contextData.clientId;

  const totalAvailableCoins = createMemo(() => {
    return v4Home?.coins.totalAvailable ?? 0;
  });

  return (
    <div
      class={` mx-2 flex flex-col items-center gap-2.5 rounded-b-[32px] ${clientConfig?.payoutsEnabled ? "bg-[linear-gradient(180deg,_#FFF_47.73%,_rgba(255,_255,_255,_0.00)_100%),_linear-gradient(77deg,_#FFF1D5_3.77%,_#D1D6DA_81.54%)]" : "app-header bg-sdkAppHeader"} p-3`}
    >
      <div class="flex w-full items-center justify-between">
        <Switch>
          <Match when={contextData.isLoggedIn && contextData.mode == "hubble"}>
            <HubbleFullSvg class="h-5 self-center pl-3.5" />
          </Match>
          <Match
            when={
              contextData.isLoggedIn &&
              contextData.mode == "sdk" &&
              contextData.localClientConfig.disableHomePageBackButton
            }
          >
            <BackButton />
          </Match>
        </Switch>
        <Switch>
          <Match
            when={
              contextData.isLoggedIn &&
              contextData.mode == "sdk" &&
              clientConfig?.clientBrandNameLogoUrl
            }
          >
            <img
              src={clientConfig?.clientBrandNameLogoUrl}
              class={`h-13 w-24 align-baseline ${contextData.isLoggedIn && contextData.mode == "sdk" && !contextData.localClientConfig.disableHomePageBackButton ? "ml-6" : ""}`}
              style={{ "object-fit": "contain" }}
              alt={
                clientLogoDescriptions[
                clientConfig?.partnerName as keyof typeof clientLogoDescriptions
                ] ?? ""
              }
            />
          </Match>
        </Switch>
        <div class="ml-auto flex gap-2.5">
          <Show
            when={
              contextData.isLoggedIn &&
              contextData.v4HomePage &&
              contextData.clientConfig?.coinsEnabled &&
              !context.isClientDonationType()
            }
          >
            <IconButton>
              <span class="flex flex-row items-center gap-1 text-f12Bold">
                <img
                  src={
                    clientConfig?.coinIconUrl
                      ? clientConfig?.coinIconUrl
                      : defaultCoin
                  }
                  class="h-[19.5px] w-[19.5px]"
                />
                <Show
                  when={config.customizedSDKClients.trimCoinsOnAppHeader.includes(
                    clientId || ""
                  )}
                  fallback={toIndianNumber(v4Home?.coins.totalAvailable ?? 0)}
                >
                  {roundOffCoins(v4Home?.coins.totalAvailable ?? 0)}
                </Show>
              </span>
            </IconButton>
          </Show>
          <IconButton
            label="home menu"
            onClick={(event) => {
              pushPage(
                () => <HomeMenu />,
                {
                  isModal: true,
                  event: event,
                  alignment: PageAlignment.topRight,
                  offset: { top: 20, right: 20 },
                },
                HubbleRoute.HOME_MENU
              );
            }}
          >
            <div class="fill-baseDark">
              <HorizontalThreeDot />
            </div>
          </IconButton>
        </div>
      </div>
      <Show when={contextData.isLoggedIn && clientConfig?.payoutsEnabled}>
        <hr class="my-3 h-px w-full border-t border-[#EDEEF1]" />
        <div class="flex w-full items-center justify-between gap-4">
          <div class="flex w-[218px] items-center justify-start gap-2">
            <img src={defaultCoin} class="h-[42px] w-[42px]" alt="" />
            <div>
              <p class="text-[10px] font-semibold text-textDark">
                Incentive balance
              </p>
              <p class="text-h5 text-coinPercentageOrange">
                <Show
                  when={config.customizedSDKClients.trimCoinsOnAppHeader.includes(
                    clientId || ""
                  )}
                  fallback={toRupees(totalAvailableCoins())}
                >
                  ₹{roundOffCoins(totalAvailableCoins())}
                </Show>
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              pushPage(
                () => <BankTransferDetails />,
                {
                  isModal: false,
                  alignment: PageAlignment.topRight,
                  offset: { top: 20, right: 20 },
                },
                HubbleRoute.BANK_TRANSFER
              );
            }}
            class="h-9 text-nowrap rounded-[39px] border border-baseTertiaryDark bg-baseTertiaryLight px-3 py-2 text-f12Bold text-textDark"
          >
            Bank transfer
          </button>
        </div>
      </Show>
    </div>
  );
}

function BackButton() {
  return (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();

        routerLog(`Homepage back clicked.`);

        sdkEventManager.sendEvent(HubbleEvent.EXITED, {
          through: "home_back_button",
        });

        postCloseEventToAllExternalListeners();

        goBack();
      }}
    >
      <div class="fill-baseDark">
        <LeftArrow />
      </div>
    </IconButton>
  );
}

function PopularBrandCard({ product }: { product: HubbleBrandProduct }) {
  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;

  const clientId = contextData.clientId;

  return (
    <div
      class="flex cursor-pointer flex-col items-center"
      onClick={() => openVoucherL2({ productId: product.id })}
    >
      <HubbleImage
        class="h-20 w-20 rounded-3xl"
        src={
          config.customizedSDKClients.squareAssetsForPopularBrands.includes(
            clientId || ""
          )
            ? (product.voucherProduct.sqLogoUrl ??
              "https://app.myhubble.money/static/brand-assets/" +
              product.brandKey +
              "/top_brand.png")
            : "https://app.myhubble.money/static/brand-assets/" +
            product.brandKey +
            "/top_brand.png"
        }
        alt={product.voucherProduct.title}
        errorImage={placeholderCard}
      />
      <Show when={!clientConfig?.payoutsEnabled}>
        <Show
          when={context.isClientDonationType()}
          fallback={
            <div class="mt-2.5 text-nowrap text-h5 text-successDark">
              {product.voucherProduct.discountPercentage}
              <span class="text-mediumBolder uppercase">% off</span>
            </div>
          }
        >
          <div class="flex items-end gap-1">
            <div class="mt-2.5 text-nowrap text-h5 text-successDark">
              {product.voucherProduct.discountPercentage}
              <span class="text-mediumBolder uppercase">{" %"}</span>
            </div>
            <HubbleImage src={donationIcon} alt="" class="mb-1 h-5 w-5" />
          </div>
        </Show>
      </Show>
    </div>
  );
}

function PopularBrands(props: { products: HubbleBrandProduct[] }) {
  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;
  return (
    <div class="flex flex-col items-start">
      <div class="title-primary no-scrollbar mb-1 ml-6 py-5 text-h5Bold text-textDark">
        Popular brands
      </div>
      <div class={`no-scrollbar`}>
        <div
          style={{
            "grid-template-columns": `repeat(${Math.ceil(props.products.length / 2)}, minmax(80px, 1fr))`,
          }}
          //
          class={`no-scrollbar grid w-screen grid-flow-col grid-rows-2 overflow-x-scroll px-5 pb-3`}
          classList={{
            "gap-x-8 gap-y-3": !clientConfig?.payoutsEnabled,
            "gap-6": clientConfig?.payoutsEnabled,
          }}
        >
          <For each={props.products}>
            {(product) => <PopularBrandCard product={product} />}
          </For>
        </div>
      </div>
    </div>
  );
}

function BrandsCountText() {
  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;
  return (
    <div
      class={`no-scrollbar flex flex-row items-center justify-start  p-5 pr-0`}
      classList={{
        "gap-8": !clientConfig?.payoutsEnabled,
        "gap-3": clientConfig?.payoutsEnabled,
      }}
    >
      <div class="title-section text-left text-h5Bold">
        <Switch>
          <Match when={!clientConfig?.payoutsEnabled}>
            {"Save on "}
            <br />
            {brandsCount + "+ brands"}
          </Match>

          <Match when={clientConfig?.payoutsEnabled}>
            Redeem incentive on <br /> 400+ brands
          </Match>
        </Switch>
      </div>
      <img
        src={assetVictoryIcon}
        class="w-[200px]"
        classList={{
          "max-w-[65%]": !clientConfig?.payoutsEnabled,
          "max-w-[38%]": clientConfig?.payoutsEnabled,
        }}
      />
    </div>
  );
}

function SeeAllBrandsCta() {
  return (
    <button class="mt-3 w-full px-5" onClick={openSearchPage}>
      <div class="secondary-radius flex flex-row items-center justify-center rounded-full border-[1px] border-basePrimaryDark p-3">
        <div class="button-primary font-semibold text-basePrimaryDark">
          See {brandsCount}+ brands
        </div>
        <div class="w-2" />
        <img src={seeBrandsArrow} alt="" />
      </div>
    </button>
  );
}

function AllBrands(props: {
  products: HubbleBrandProduct[];
  homepageMetaData: HomepageMetaData;
}) {
  const horizontalSectionsResourceMap =
    props.homepageMetaData.horizontalSectionsResourceMap;

  const horizontalSections = horizontalSectionsResourceMap.map(
    (resourceMap) => {
      return <HorizontalSection horizontalSectionsResourceMap={resourceMap} />;
    }
  );

  const items = createMemo(() => {
    const mixedItems: any[] = [];
    const products = props.products;
    const sections = [...horizontalSections];

    for (let i = 0; i < products.length; i++) {
      mixedItems.push(<LargeProductCard product={products[i]} />);
      if ((i + 1) % 4 === 0 && sections.length > 0) {
        mixedItems.push(sections.shift());
      }
    }

    if (sections.length > 0) {
      mixedItems.push(...sections);
    }

    return mixedItems;
  });

  return (
    <div class="flex flex-col gap-5">
      <For each={items()}>{(item) => item}</For>
    </div>
  );
}

function BrandCountDivider() {
  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;

  return (
    <div class="mx-5 flex items-center gap-2">
      <Switch>
        <Match
          when={
            !context.isClientDonationType() && !clientConfig?.payoutsEnabled
          }
        >
          <div class="caption whitespace-pre text-f12Bold uppercase text-textNormal">
            {"Get discount on " + brandsCount}+ brands
          </div>
        </Match>
        <Match when={context.isClientDonationType()}>
          <div class="whitespace-pre text-f12Bold uppercase text-textNormal">
            Shop and donate
          </div>
        </Match>
        <Match when={clientConfig?.payoutsEnabled}>
          <div class="whitespace-pre text-f12Bold uppercase text-textNormal">
            Redeem on 400+ brands
          </div>
        </Match>
      </Switch>
      <div class="h-px w-full bg-baseTertiaryMedium"></div>
      <Show
        when={
          (context.data.isLoggedIn &&
            context.data.clientConfig.showHubbleLogoOnHomeCategories) ||
          clientConfig?.payoutsEnabled
        }
      >
        <HubbleBranding verticalOrientation={true} />
      </Show>
    </div>
  );
}

export default function Home() {
  const searchParams = new URLSearchParams(window.location.search);
  const isSourceKbBanner = false;
  searchParams.get("utm_source") === "khatabook_banner";
  const { data, isClientDonationType, homepageMetaDataResource } =
    useClassicContext();

  createEffect(() => {
    hostOnAppLoaded();
  });

  const [homepageMetaData, homepageMetaDataActions] = homepageMetaDataResource;

  return (
    <Switch>
      <Match when={homepageMetaData.state == "pending"}>
        <Show
          when={isClientDonationType()}
          fallback={
            <div class="left-0 top-0 z-[100] flex h-full w-full items-center justify-center bg-baseTertiaryLight">
              <ThreeDotLoader class="!bg-basePrimaryDark" />
            </div>
          }
        >
          <KettoLoadingState />
        </Show>
      </Match>
      <Match when={homepageMetaData.state == "ready"}>
        <HomePageContent homepageMetaData={homepageMetaData()!} />
      </Match>
      <Match when={homepageMetaData.state == "errored"}>
        <SimpleRetryView
          message={homepageMetaData.error.message}
          onRetry={() => homepageMetaDataActions.refetch()}
        />
      </Match>
    </Switch>
  );

  function HomePageContent(props: { homepageMetaData: HomepageMetaData }) {
    return (
      <>
        <div class="no-scrollbar absolute left-0 right-0 top-0 h-svh overflow-y-scroll bg-baseTertiaryLight pb-24">
          <AppHeader />
          <SearchOverlay close={() => { }} />
          <BrandCountDivider />
          <CategoryCardScroll
            categories={props.homepageMetaData.categoryColumns}
          />
          <Banners banners={props.homepageMetaData.banners} showFooter />
          <PopularBrands products={props.homepageMetaData.popularProducts} />
          <SeeAllBrandsCta />
          <BrandsCountText />
          <AllBrands
            products={props.homepageMetaData.topProducts}
            homepageMetaData={props.homepageMetaData}
          />
          <SeeAllBrandsCta />
        </div>
        <Show
          when={data.isLoggedIn && data.clientConfig.showHubbleLogoOnBottom}
        >
          <div class="absolute bottom-0 left-0 right-0">
            <HubbleBranding />
          </div>
        </Show>
      </>
    );
  }
}

function KettoLoadingState() {
  return (
    <div class="flex min-h-screen w-screen flex-col items-center justify-normal gap-2 bg-[#F7F8F4] text-center">
      <div class="h-[300px] w-full">
        <HubbleImage
          src={kettoSDKLoadingImage}
          alt=""
          class="h-[300px] w-full"
        />
      </div>
      <div class="mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-[#F0EFE3] align-middle">
        <HubbleImage
          src={donationIconColored}
          alt=""
          class="h-[55px] w-[55px]"
        />
      </div>
      <div class="px-5 text-h2">Support a cause every time you shop</div>
      <div class="font-light px-12 text-normal">
        Buy gift cards from 400+ brands & donate up to 10% at no extra cost
      </div>
    </div>
  );
}
